import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { useConfigStore } from "@/stores/config";
import { useInspectionStore } from "@/stores/inspection";
import { INSPECTION_COMMENTS } from "@/constants/Inspections";
import { getReportFooter, getReportHeader } from "@/reports/ReportUtil";
import { styles } from "@/reports/reportStyles";
import { getFormattedDateStringNoTime } from "@/composables/dateField.js";
import { clone } from "@/util/clone";
import { convertToInt } from "@/composables/util";

function setLoadRatingsTunnelDocDefinition(structure) {
  const configStore = useConfigStore();
  const inspectionStore = useInspectionStore();
  let docDefinition = {
    header: function () {
      return getReportHeader("Load Ratings Page", structure);
    },
    footer: function (currentPage, pageCount) {
      return getReportFooter(currentPage, pageCount);
    },
    pageMargins: [20, 70, 20, 50],
    content: [
      { text: "", pageHeaderText: "Load Ratings Page" },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Load Ratings Details",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
      },
      {
        columns: [
          {
            width: "30%",
            text: "L.1 Rating Method: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.TUNNEL_LOAD_RATING_METHOD,
              structure?.tunnelLoadRating?.ratingMethod,
            ),
            margin: [0, 5, 0, 0],
          },
          {
            width: "30%",
            text: "L.7 Posting Load - Type 3: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: structure?.tunnelLoadRating?.type3PostLoad,
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "L.2 Inventory Rating Factor: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: structure?.tunnelLoadRating?.invLrFactor,
            margin: [0, 5, 0, 0],
          },
          {
            width: "30%",
            text: "L.8 Posting Load - Type 3S2: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: structure?.tunnelLoadRating?.type3S2PostLoad,
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "L.3 Operating Rating Factor: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: structure?.tunnelLoadRating?.opLrFactor,
            margin: [0, 5, 0, 0],
          },
          {
            width: "30%",
            text: "L.9 Posting Load - Type 3-3: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: structure?.tunnelLoadRating?.type33PostLoad,
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "L.4 Load Posting Status: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.POSTING_STATUS,
              structure?.postings?.find((posting) => posting.active === "1")?.postStatus,
            ),
            margin: [0, 5, 0, 0],
          },
          {
            width: "30%",
            text: "L.10 Height Restriction: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(REFERENCE_TABLE.YES_NO, structure?.tunnelLoadRating?.heightRestr),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "L.5 Posting Load - Gross: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: structure?.tunnelLoadRating?.grossPostLoad,
            margin: [0, 5, 0, 0],
          },
          {
            width: "30%",
            text: "L.11 Hazardous Material Restriction: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(REFERENCE_TABLE.YES_NO, structure?.tunnelLoadRating?.hazmatRestr),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "L.6 Posting Load - Axle: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: structure?.tunnelLoadRating?.axlePostLoad,
            margin: [0, 5, 0, 0],
          },
          {
            width: "30%",
            text: "L.12 Other Restrictions: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(REFERENCE_TABLE.YES_NO, structure?.tunnelLoadRating?.otherRestr),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Posting History",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 5, 0, 0],
      },
    ],
    styles: styles,
    defaultStyle: {
      fontSize: 10,
    },
  };
  docDefinition.content.push({
    table: {
      widths: ["12%", "*", "*", "15%", "18%", "*"],
      headerRows: 1,
      body: getPostingHistoryTable(configStore, structure, getFormattedDateStringNoTime, inspectionStore),
    },
    margin: [0, 5, 0, 0],
  });
  return docDefinition;
}
function setLoadRatingsDocDefinition(structure) {
  const inspectionStore = useInspectionStore();
  const configStore = useConfigStore();
  const getFloatValue = (value) => {
    //replace commas in the pasted value so parsefloat will work
    value = value?.toString()?.replaceAll(",", "");
    return Number.parseFloat(value) || Number.parseFloat(value) === 0 ? Number.parseFloat(value) : null;
  };

  const isRF = (ratingAnalysisMethod, loadType, isNBI) => {
    if (
      ratingAnalysisMethod == "8" ||
      ratingAnalysisMethod == "D" ||
      ratingAnalysisMethod == "E" ||
      ratingAnalysisMethod == "F"
    ) {
      return true;
    } else if (isNBI && ratingAnalysisMethod == "6" && loadType == "D") {
      return true;
    } else {
      return false;
    }
  };

  let docDefinition = {
    header: function () {
      return getReportHeader("Load Ratings Page", structure);
    },
    footer: function (currentPage, pageCount) {
      return getReportFooter(currentPage, pageCount);
    },
    pageMargins: [20, 70, 20, 50],
    content: [
      { text: "", pageHeaderText: "Load Ratings Page" },
      {
        columns: [
          {
            width: "27%",
            text: "LR.01 Load Rating Rev. Recom'd: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "2%",
            text: inspectionStore.getReportCheckbox(structure?.userInspection?.recommendRatingReview),
            margin: [0, 5, 0, 0],
            style: "customFontAwesome",
          },
          {
            width: "21%",
            text: "LR.02 Reviewer Action: ",
            bold: true,
            margin: [5, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.REVIEWER_ACTION,
              structure?.userInspection?.lrReviewAction,
            ),
            margin: [0, 5, 0, 0],
          },
          {
            width: "23%",
            text: "LR.03 Posting Rev. Recom'd: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "2%",
            text: inspectionStore.getReportCheckbox(structure?.userInspection?.postReviewRec),
            style: "customFontAwesome",
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "45%",
            text: "Inspection Team Notes: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "5%",
            text: "",
            margin: [0, 5, 0, 0],
          },
          {
            width: "45%",
            text: "Posting Review Recommended Summary: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: "",
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "45%",
            text: {
              text: structure?.inspectionComments?.find(
                (element) => element?.commentType == INSPECTION_COMMENTS.REVIEW_RECOMMENDED_DUE_TO_COMMENTS,
              )?.notes,
            },
          },
          {
            width: "5%",
            text: {
              text: "",
            },
          },
          {
            width: "*",
            text: {
              text: structure?.inspectionComments?.find(
                (element) => element?.commentType == INSPECTION_COMMENTS.LOAD_RATING_POSTING_RECOMMENDED_SUMMARY,
              )?.notes,
            },
          },
        ],
      },
      {
        columns: [
          {
            width: "45%",
            text: "Previous Inspection Team Notes: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "5%",
            text: "",
            margin: [0, 5, 0, 0],
          },
          {
            width: "45%",
            text: "Previous Load Rating Engineer Notes: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: "",
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "45%",
            text: {
              text: structure?.inspectionCommentsPrev?.find(
                (element) => element?.commentType == INSPECTION_COMMENTS.REVIEW_RECOMMENDED_DUE_TO_COMMENTS,
              )?.notes,
            },
          },
          {
            width: "5%",
            text: {
              text: "",
            },
          },
          {
            width: "*",
            text: {
              text: structure?.inspectionCommentsPrev?.find(
                (element) => element?.commentType == INSPECTION_COMMENTS.LOAD_RATING_REVIEW,
              )?.notes,
            },
          },
        ],
      },
      {
        columns: [
          {
            width: "19%",
            text: "LR.04 Calculation Date: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "31%",
            text: getFormattedDateStringNoTime(structure?.userInspection?.ratingDate),
            margin: [0, 5, 0, 0],
          },
          {
            width: "31%",
            text: "LR.05 Assigned Rating Approval Date: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: getFormattedDateStringNoTime(structure?.userInspection?.ratingApprDate),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "35%",
            text: "LR.06 Assigned Rating Approval Engineer: ",
            bold: true,
            margin: [0, 5, 0, 5],
          },
          {
            width: "*",
            text: structure?.userInspection?.approverName,
            margin: [0, 5, 0, 5],
          },
        ],
      },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Load Ratings Details",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
      },
    ],
    styles: styles,
    defaultStyle: {
      fontSize: 10,
    },
  };

  const detailsValue = getDetails(structure)?.sort((a, b) => a?.loadType?.localeCompare(b?.loadType)) || [];

  let loadRatings = [];
  loadRatings.push([
    {
      text: "B.EP.01\n  Load\n Type",
      style: ["tableHeader"],
    },
    {
      text: "LR.07\n NBI\n",
      style: ["tableHeader"],
    },
    {
      text: "LR.08\n Load Rating\n Method",
      style: ["tableHeader"],
    },
    {
      text: "LR.15\n IR\n Tons",
      style: ["tableHeader"],
    },
    {
      text: "LR.16\n OR\n Tons",
      style: ["tableHeader"],
    },
    {
      text: "LR.17\n SLC\n Tons",
      style: ["tableHeader"],
    },
    {
      text: "LR.18\n IR\n RF",
      style: ["tableHeader"],
    },
    {
      text: "LR.19\n OR\n RF",
      style: ["tableHeader"],
    },
    {
      text: "LR.23\n Rating Dataset\n",
      style: ["tableHeader"],
    },
  ]);
  for (const element of detailsValue ?? []) {
    loadRatings = updateLoadRatings(loadRatings, element, configStore, getFormattedDateStringNoTime);
  }
  docDefinition.content.push(
    {
      table: {
        headerRows: 1,
        body: loadRatings,
      },
      margin: [0, 5, 0, 0],
    },
    {
      table: {
        headerRows: 0,
        widths: ["100%"],
        body: [
          [
            {
              text: "NBI & Alternate Ratings",
              fillColor: "#ebebeb",
              alignment: "center",
              style: ["sectionHeader"],
            },
          ],
        ],
      },
      layout: "noBorders",
      margin: [0, 5, 0, 5],
    },
    {
      columns: [
        {
          width: "*",
          text: "NBI: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
      ],
    },
    {
      columns: [
        {
          width: "23%",
          text: "B.LR.01 Design Load: ",
          bold: true,
          margin: [3, 3, 0, 0],
        },
        {
          width: "*",
          text: configStore.getReferenceValue(REFERENCE_TABLE.DESIGN_LOAD, structure?.structureBridge?.designLoad),
          margin: [0, 3, 0, 0],
        },
        {
          width: "23%",
          text: "B.LR.03 Rating Date: ",
          bold: true,
          margin: [3, 3, 0, 0],
        },
        {
          width: "*",
          text: structure?.nbiAndAlternateRating?.inspectionDate,
          margin: [0, 3, 0, 0],
        },
      ],
    },
    {
      columns: [
        {
          width: "23%",
          text: "B.LR.05 Inventory Rating: ",
          bold: true,
          margin: [3, 3, 0, 0],
        },
        {
          width: "*",
          text: isRF(
            structure?.nbiAndAlternateRating?.nbi1RatingAnalysisMethod,
            structure?.nbiAndAlternateRating?.nbi1LoadType,
            true,
          )
            ? getFloatValue(structure?.nbiAndAlternateRating?.nbi1IRLoadFactor)?.toFixed(2)
            : getFloatValue(structure?.nbiAndAlternateRating?.nbi1IRLoad,
              )?.toFixed(0) + " Tons",
          margin: [0, 3, 0, 0],
        },
        {
          width: "23%",
          text: "B.LR.04 Operating Rating Method: ",
          bold: true,
          margin: [3, 3, 0, 0],
        },
        {
          width: "*",
          text: configStore.getReferenceValue(
            REFERENCE_TABLE.OPERATING_RATING_METHOD,
            structure?.nbiAndAlternateRating?.nbi1OperatingRatingMethod,
          ),
          margin: [0, 3, 0, 0],
        },
      ],
    },
    {
      columns: [
        {
          width: "23%",
          text: "B.LR.06 Operating Rating: ",
          bold: true,
          margin: [3, 3, 0, 0],
        },
        {
          width: "*",
          text: isRF(
            structure?.nbiAndAlternateRating?.nbi1RatingAnalysisMethod,
            structure?.nbiAndAlternateRating?.nbi1LoadType,
            true,
          )
            ? getFloatValue(structure?.nbiAndAlternateRating?.nbi1ORLoadFactor)?.toFixed(2)
            : getFloatValue(structure?.nbiAndAlternateRating?.nbi1ORLoad,
              )?.toFixed(0) + " Tons",
          margin: [0, 3, 0, 0],
        },
        {
          width: "23%",
          text: "B.LR.02 Design Method: ",
          bold: true,
          margin: [3, 3, 0, 0],
        },
        {
          width: "*",
          text: configStore.getReferenceValue(REFERENCE_TABLE.DESIGN_METHOD, structure?.userBridge?.designMethod),
          margin: [0, 3, 0, 0],
        },
      ],
    },
    {
      columns: [
        {
          width: "23%",
          text: "B.LR.07 Controlling Legal Load Rating Factor: ",
          bold: true,
          margin: [3, 3, 0, 0],
        },
        {
          width: "*",
          text: structure?.nbiAndAlternateRating?.controllingLegalLoadRatingFactor,
          margin: [0, 3, 0, 0],
        },
        {
          width: "23%",
          text: "B.LR.08 Routine Permit Loads: ",
          bold: true,
          margin: [3, 3, 0, 0],
        },
        {
          width: "*",
          text: configStore.getReferenceValue(
            REFERENCE_TABLE.ROUTINE_PERMIT_LOADS,
            structure?.structureBridge?.routinePermitLoads,
          ),
          margin: [0, 3, 0, 0],
        },
      ],
    },
    {
      columns: [
        {
          width: "23%",
          text: "LR.25 Controlling Penndot Legal Load Rating Factor: ",
          bold: true,
          margin: [3, 3, 0, 0],
        },
        {
          width: "*",
          text: structure?.nbiAndAlternateRating?.penndotControllingLegalLoadRatingFactor,
          margin: [0, 3, 0, 0],
        },
      ],
    },
    {
      columns: [
        {
          width: "*",
          text: "Alternate: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
      ],
    },
    {
      columns: [
        {
          width: "23%",
          text: "LR.26 H20 Inv. Rating: ",
          bold: true,
          margin: [3, 3, 0, 0],
        },
        {
          width: "*",
          text: isRF(
            structure?.nbiAndAlternateRating?.h20RatingAnalysisMethod,
            structure?.nbiAndAlternateRating?.h20LoadType,
            true,
          )
            ? getFloatValue(structure?.nbiAndAlternateRating?.h20IRLoadFactor)?.toFixed(2)
            : getFloatValue(structure?.nbiAndAlternateRating?.h20IRLoad,
              )?.toFixed(0) + " Tons",
          margin: [0, 3, 0, 0],
        },
      ],
    },
    {
      columns: [
        {
          width: "23%",
          text: "LR.27 H20 Oper. Rating: ",
          bold: true,
          margin: [3, 3, 0, 0],
        },
        {
          width: "*",
          text: isRF(
            structure?.nbiAndAlternateRating?.h20RatingAnalysisMethod,
            structure?.nbiAndAlternateRating?.h20LoadType,
            true,
          )
            ? getFloatValue(structure?.nbiAndAlternateRating?.h20ORLoadFactor)?.toFixed(2)
            : getFloatValue(structure?.nbiAndAlternateRating?.h20ORLoad,
              )?.toFixed(0) + " Tons",
          margin: [0, 3, 0, 0],
        },
        {
          width: "23%",
          text: "LR.28 H20 Oper. Type: ",
          bold: true,
          margin: [3, 3, 0, 0],
        },
        {
          width: "*",
          text: configStore.getReferenceValue(
            REFERENCE_TABLE.LOAD_RATING_METHOD,
            structure?.nbiAndAlternateRating?.h20RatingAnalysisMethod,
          ),
          margin: [0, 3, 0, 0],
        },
      ],
    },
    {
      columns: [
        {
          width: "23%",
          text: "LR.29 ML80 Inv. Rating: ",
          bold: true,
          margin: [3, 3, 0, 0],
        },
        {
          width: "*",
          text: isRF(
            structure?.nbiAndAlternateRating?.ml80RatingAnalysisMethod,
            structure?.nbiAndAlternateRating?.ml80LoadType,
            true,
          )
            ? getFloatValue(structure?.nbiAndAlternateRating?.ml80IRLoadFactor)?.toFixed(2)
            : getFloatValue(structure?.nbiAndAlternateRating?.ml80IRLoad,
              )?.toFixed(0) + " Tons",
          margin: [0, 3, 0, 0],
        },
      ],
    },
    {
      columns: [
        {
          width: "23%",
          text: "LR.30 ML80 Oper. Rating: ",
          bold: true,
          margin: [3, 3, 0, 0],
        },
        {
          width: "*",
          text: isRF(
            structure?.nbiAndAlternateRating?.ml80RatingAnalysisMethod,
            structure?.nbiAndAlternateRating?.ml80LoadType,
            true,
          )
            ? getFloatValue(structure?.nbiAndAlternateRating?.ml80ORLoadFactor)?.toFixed(2)
            : getFloatValue(structure?.nbiAndAlternateRating?.ml80ORLoad,
              )?.toFixed(0) + " Tons",
          margin: [0, 3, 0, 0],
        },
        {
          width: "23%",
          text: "LR.31 ML80 Oper. Type: ",
          bold: true,
          margin: [3, 3, 0, 0],
        },
        {
          width: "*",
          text: configStore.getReferenceValue(
            REFERENCE_TABLE.LOAD_RATING_METHOD,
            structure?.nbiAndAlternateRating?.ml80RatingAnalysisMethod,
          ),
          margin: [0, 3, 0, 0],
        },
      ],
    },
    {
      columns: [
        {
          width: "23%",
          text: "LR.32 TK527 Inv. Rating: ",
          bold: true,
          margin: [3, 3, 0, 0],
        },
        {
          width: "*",
          text: isRF(
            structure?.nbiAndAlternateRating?.tk527RatingAnalysisMethod,
            structure?.nbiAndAlternateRating?.tk527LoadType,
            true,
          )
            ? getFloatValue(structure?.nbiAndAlternateRating?.tk527IRLoadFactor)?.toFixed(2)
            : getFloatValue(structure?.nbiAndAlternateRating?.tk527IRLoad,
              )?.toFixed(0) + " Tons",
          margin: [0, 3, 0, 0],
        },
      ],
    },
    {
      columns: [
        {
          width: "23%",
          text: "LR.33 TK527 Oper. Rating: ",
          bold: true,
          margin: [3, 3, 0, 0],
        },
        {
          width: "*",
          text: isRF(
            structure?.nbiAndAlternateRating?.tk527RatingAnalysisMethod,
            structure?.nbiAndAlternateRating?.tk527LoadType,
            true,
          )
            ? getFloatValue(structure?.nbiAndAlternateRating?.tk527ORLoadFactor)?.toFixed(2)
            : getFloatValue(structure?.nbiAndAlternateRating?.tk527ORLoad,
              )?.toFixed(0) + " Tons",
          margin: [0, 3, 0, 0],
        },
        {
          width: "23%",
          text: "LR.34 TK527 Oper. Type: ",
          bold: true,
          margin: [3, 3, 0, 0],
        },
        {
          width: "*",
          text: configStore.getReferenceValue(
            REFERENCE_TABLE.LOAD_RATING_METHOD,
            structure?.nbiAndAlternateRating?.tk527RatingAnalysisMethod,
          ),
          margin: [0, 3, 0, 0],
        },
      ],
    },
    {
      table: {
        headerRows: 0,
        widths: ["100%"],
        body: [
          [
            {
              text: "Posting History",
              fillColor: "#ebebeb",
              alignment: "center",
              style: ["sectionHeader"],
            },
          ],
        ],
      },
      layout: "noBorders",
      margin: [0, 5, 0, 0],
    },
    {
      table: {
        widths: ["12%", "*", "*", "11%", "13%", "*"],
        headerRows: 1,
        body: getPostingHistoryTable(configStore, structure, getFormattedDateStringNoTime, inspectionStore),
      },
      margin: [0, 5, 0, 0],
    },
  );

  return docDefinition;
}
function getDetails(structure) {
  let detailsValue = [];
  if (!structure?.ratingLoads?.[0]) {
    structure.ratingLoads = structure?.ratingLoads?.filter((a) => !!a);
  }
  detailsValue = clone(structure.ratingLoads);
  sortDetails(detailsValue);
  return detailsValue;
}
function sortDetails(arr) {
  arr.value = arr.value?.sort(function (a, b) {
    return (
      convertToInt(a?.loadType) - convertToInt(b?.loadType) ||
      convertToInt(a?.nbiRatingInd) - convertToInt(b?.nbiRatingInd)
    );
  });
}
function getPostingHistoryTable(configStore, structure, getFormattedDateStringNoTime, inspectionStore) {
  let postingHistory = clone(structure?.postings) || [];
  postingHistory?.sort(function (a, b) {
    return new Date(b?.postStatusDate) - new Date(a?.postStatusDate);
  });
  let postingHistoryTable = [];
  postingHistoryTable.push([
    {
      text: "B.PS.02\n  Status\n Date",
      style: ["tableHeader"],
    },
    {
      text: "PS.01\n Posting\n Status",
      style: ["tableHeader"],
    },
    {
      text: "PS.02\n Special Restr.\n Posting",
      style: ["tableHeader"],
    },
    {
      text: "PS.04\n Posted Wt.\n Limit (Tons)",
      style: ["tableHeader"],
    },
    {
      text: "PS.05\n Posted Limit\n Comb. (Tons)",
      style: ["tableHeader"],
    },
    {
      text: "PS.06\n Posting\n Reason",
      style: ["tableHeader"],
    },
  ]);
  for (const element of postingHistory ?? []) {
    postingHistoryTable.push(
      [
        {
          text: getFormattedDateStringNoTime(element?.postStatusDate),
          style: ["tableBody"],
        },
        {
          text: element?.postStatus
            ? configStore.getReferenceValue(REFERENCE_TABLE.POSTING_STATUS, element?.postStatus)
            : "",
          style: ["tableBody"],
        },
        {
          text: element?.specRestrictPost
            ? configStore.getReferenceValue(REFERENCE_TABLE.SPECIAL_RESTRICT_POST, element?.specRestrictPost)
            : "",
          style: ["tableBody"],
        },
        {
          text: element?.postLimitWeight,
          style: ["tableBody"],
        },
        {
          text: element?.postLimitComb,
          style: ["tableBody"],
        },
        {
          text: element?.postReason
            ? configStore.getReferenceValue(REFERENCE_TABLE.POST_REASON, element?.postReason)
            : "",
          style: ["tableBody"],
        },
      ],
      [
        {
          colSpan: 6,
          stack: [
            {
              columns: [
                {
                  table: {
                    widths: ["55%", "*"],
                    body: [
                      [
                        {
                          text: "B.PS.02 Status Date: ",
                          bold: true,
                          margin: [3, 3, 0, 0],
                        },
                        {
                          text: getFormattedDateStringNoTime(element?.postStatusDate),
                          margin: [0, 3, 0, 0],
                        },
                      ],
                      [
                        {
                          text: "PS.01 Posting Status: ",
                          bold: true,
                          margin: [3, 3, 0, 0],
                        },
                        {
                          text: configStore.getReferenceValue(REFERENCE_TABLE.POSTING_STATUS, element?.postStatus),
                          margin: [0, 3, 0, 0],
                        },
                      ],
                      [
                        {
                          text: "B.PS.01 Posting Type: ",
                          bold: true,
                          margin: [3, 3, 0, 0],
                        },
                        {
                          text: configStore.getReferenceValue(REFERENCE_TABLE.POSTING_TYPE, element?.snbiPostStatus),
                          margin: [0, 3, 0, 0],
                        },
                      ],
                      [
                        {
                          text: "B.PS.01 Posting Condition: ",
                          bold: true,
                          margin: [3, 3, 0, 0],
                        },
                        {
                          text: configStore.getReferenceValue(
                            REFERENCE_TABLE.POSTING_CONDITION,
                            element?.snbiPostCondition,
                          ),
                          margin: [0, 3, 0, 0],
                        },
                      ],
                      [
                        {
                          text: "PS.02 Special Restr. Posting: ",
                          bold: true,
                          margin: [3, 3, 0, 0],
                        },
                        {
                          text: configStore.getReferenceValue(
                            REFERENCE_TABLE.SPECIAL_RESTRICT_POST,
                            element?.specRestrictPost,
                          ),
                          margin: [0, 3, 0, 0],
                        },
                      ],
                      [
                        {
                          text: "PS.04 Posted Wt. Limit: ",
                          bold: true,
                          margin: [3, 3, 0, 0],
                        },
                        {
                          text: element?.postLimitWeight + " Tons",
                          margin: [0, 3, 0, 0],
                        },
                      ],
                      [
                        {
                          text: "PS.05 Posted Limit Comb.: ",
                          bold: true,
                          margin: [3, 3, 0, 0],
                        },
                        {
                          text: element?.postLimitComb + " Tons",
                          margin: [0, 3, 0, 0],
                        },
                      ],
                    ],
                  },
                  layout: "noBorders",
                },
                {
                  table: {
                    widths: ["55%", "*"],
                    body: [
                      [
                        {
                          text: "PS.06 Posting Reason: ",
                          bold: true,
                          margin: [3, 3, 0, 0],
                        },
                        {
                          text: configStore.getReferenceValue(REFERENCE_TABLE.POST_REASON, element?.postReason),
                          margin: [0, 3, 0, 0],
                        },
                      ],
                      [
                        {
                          text: "PS.07 Field Conditions: ",
                          bold: true,
                          margin: [3, 3, 0, 0],
                        },
                        {
                          text: configStore.getReferenceValue(REFERENCE_TABLE.FIELD_COND, element?.fieldCond),
                          margin: [0, 3, 0, 0],
                        },
                      ],
                      [
                        {
                          text: "PS.08 Special Conditions: ",
                          bold: true,
                          margin: [3, 3, 0, 0],
                        },
                        {
                          text: configStore.getReferenceValue(REFERENCE_TABLE.SPECIAL_CONDITIONS, element?.specCond),
                          margin: [0, 3, 0, 0],
                        },
                      ],
                      [
                        {
                          text: "PS.09 AASHTO Impact Code: ",
                          bold: true,
                          margin: [3, 3, 0, 0],
                        },
                        {
                          text: configStore.getReferenceValue(REFERENCE_TABLE.AASHTO_IMPACT_CODE, element?.impact),
                          margin: [0, 3, 0, 0],
                        },
                      ],
                      [
                        {
                          text: "PS.11 Active?: ",
                          bold: true,
                          margin: [3, 3, 0, 0],
                        },
                        {
                          text: inspectionStore.getReportCheckbox(element?.active),
                          margin: [0, 5, 0, 0],
                          style: "customFontAwesome",
                        },
                      ],
                    ],
                  },
                  layout: "noBorders",
                },
              ],
            },
          ],
        },
      ],
    );
  }
  return postingHistoryTable;
}
function updateLoadRatings(loadRatings, element, configStore, getFormattedDateStringNoTime) {
  loadRatings.push(
    [
      {
        text: element?.loadType ? configStore.getReferenceValue(REFERENCE_TABLE.LOAD_TYPE, element?.loadType) : "",
        style: ["tableBody"],
      },

      {
        text: element?.nbiRatingInd
          ? configStore.getReferenceValue(REFERENCE_TABLE.NBI_RATING, element?.nbiRatingInd)
          : "",
        style: ["tableBody"],
      },
      {
        text: element?.ratingAnalysisMethod
          ? configStore.getReferenceValue(REFERENCE_TABLE.LOAD_RATING_METHOD, element?.ratingAnalysisMethod)
          : "",
        style: ["tableBody"],
      },
      {
        text: element?.irLoad,
        style: ["tableBody"],
      },
      {
        text: element?.orLoad,
        style: ["tableBody"],
      },
      {
        text: element?.slcRating,
        style: ["tableBody"],
      },
      {
        text: Number.parseFloat(element?.irLoadFactor)
          ? Number.parseFloat(element?.irLoadFactor)?.toFixed(2)
          : element?.irLoadFactor,
        style: ["tableBody"],
      },
      {
        text: Number.parseFloat(element?.orLoadFactor)
          ? Number.parseFloat(element?.orLoadFactor)?.toFixed(2)
          : element?.orLoadFactor,
        style: ["tableBody"],
      },
      {
        text: element?.supportDataset,
        style: ["tableBody"],
      },
    ],
    [
      {
        colSpan: 9,
        stack: [
          {
            columns: [
              {
                table: {
                  body: [
                    [
                      {
                        text: "LR.04 Calculation Date: ",
                        bold: true,
                        margin: [0, 3, 0, 0],
                      },
                      {
                        text: getFormattedDateStringNoTime(element?.ratingDate),
                        margin: [0, 3, 0, 0],
                      },
                    ],
                    [
                      {
                        text: "B.EP.01 Load Type: ",
                        bold: true,
                        margin: [0, 3, 0, 0],
                      },
                      {
                        text: configStore.getReferenceValue(REFERENCE_TABLE.LOAD_TYPE, element?.loadType),
                        margin: [0, 3, 0, 0],
                      },
                    ],
                    [
                      {
                        text: "LR.07 NBI: ",
                        bold: true,
                        margin: [0, 3, 0, 0],
                      },
                      {
                        text: configStore.getReferenceValue(REFERENCE_TABLE.NBI_RATING, element?.nbiRatingInd),
                        margin: [0, 3, 0, 0],
                      },
                    ],
                    [
                      {
                        text: "LR.08 Load Rating Method: ",
                        bold: true,
                        margin: [0, 3, 0, 0],
                      },
                      {
                        text: configStore.getReferenceValue(
                          REFERENCE_TABLE.LOAD_RATING_METHOD,
                          element?.ratingAnalysisMethod,
                        ),
                        margin: [0, 3, 0, 0],
                      },
                    ],
                    [
                      {
                        text: "LR.09 Ctrl Member Type: ",
                        bold: true,
                        margin: [0, 3, 0, 0],
                      },
                      {
                        text: configStore.getReferenceValue(
                          REFERENCE_TABLE.CONTROLLING_MEMBER_TYPE,
                          element?.controlMemType,
                        ),
                        margin: [0, 3, 0, 0],
                      },
                    ],
                    [
                      {
                        text: "LR.10 Fatigue Stress Category: ",
                        bold: true,
                        margin: [0, 3, 0, 0],
                      },
                      {
                        text: configStore.getReferenceValue(
                          REFERENCE_TABLE.CONTROLLING_MEMBER_FATIGUE_STRESS_CATEGORY,
                          element?.fatigStressCat,
                        ),
                        margin: [0, 3, 0, 0],
                      },
                    ],
                    [
                      {
                        text: "LR.11 Fatigue Load Type: ",
                        bold: true,
                        margin: [0, 3, 0, 0],
                      },
                      {
                        text: configStore.getReferenceValue(REFERENCE_TABLE.FATIGUE_LOAD_TYPE, element?.fatigLoadType),
                        margin: [0, 3, 0, 0],
                      },
                    ],
                    [
                      {
                        text: "LR.12 AASHTO Man Yr: ",
                        bold: true,
                        margin: [0, 3, 0, 0],
                      },
                      {
                        text: element?.aashtoManualYear,
                        margin: [0, 3, 0, 0],
                      },
                    ],
                    [
                      {
                        text: "LR.13 AASHTO Spec Yr: ",
                        bold: true,
                        margin: [0, 3, 0, 0],
                      },
                      {
                        text: element?.aashtoSpecYear,
                        margin: [0, 3, 0, 0],
                      },
                    ],
                    [
                      {
                        text: "LR.14 Engineer: ",
                        bold: true,
                        margin: [0, 3, 0, 0],
                      },
                      {
                        text: element?.analysisEngineer,
                        margin: [0, 3, 0, 0],
                      },
                    ],
                  ],
                },
                layout: "noBorders",
              },
              {
                table: {
                  widths: ["60%", "*"],
                  body: [
                    [
                      {
                        text: "LR.15 Inventory Rating: ",
                        bold: true,
                        margin: [0, 3, 0, 0],
                      },
                      {
                        text: element?.irLoad + " Tons",
                        margin: [0, 3, 0, 0],
                      },
                    ],
                    [
                      {
                        text: "LR.16 Operating Rating: ",
                        bold: true,
                        margin: [0, 3, 0, 0],
                      },
                      {
                        text: element?.orLoad + " Tons",
                        margin: [0, 3, 0, 0],
                      },
                    ],
                    [
                      {
                        text: "LR.17 SLC Rating: ",
                        bold: true,
                        margin: [0, 3, 0, 0],
                      },
                      {
                        text: element?.slcRating + " Tons",
                        margin: [0, 3, 0, 0],
                      },
                    ],
                    [
                      {
                        text: "LR.18 IR Rating Factor: ",
                        bold: true,
                        margin: [0, 3, 0, 0],
                      },
                      {
                        text: element?.irLoadFactor ? Number.parseFloat(element?.irLoadFactor)?.toFixed(2) : "",
                        margin: [0, 3, 0, 0],
                      },
                    ],
                    [
                      {
                        text: "LR.19 OR Rating Factor: ",
                        bold: true,
                        margin: [0, 3, 0, 0],
                      },
                      {
                        text: element?.orLoadFactor ? Number.parseFloat(element?.orLoadFactor)?.toFixed(2) : "",
                        margin: [0, 3, 0, 0],
                      },
                    ],
                    [
                      {
                        text: "LR.20 Govern Crit Inventory: ",
                        bold: true,
                        margin: [0, 3, 0, 0],
                      },
                      {
                        text: configStore.getReferenceValue(
                          REFERENCE_TABLE.GOVERNING_CRITERIA,
                          element?.invRatingStressMethod,
                        ),
                        margin: [0, 3, 0, 0],
                      },
                    ],
                    [
                      {
                        text: "LR.21 Govern Crit Operating: ",
                        bold: true,
                        margin: [0, 3, 0, 0],
                      },
                      {
                        text: configStore.getReferenceValue(
                          REFERENCE_TABLE.GOVERNING_CRITERIA,
                          element?.oprRatingStressMethod,
                        ),
                        margin: [0, 3, 0, 0],
                      },
                    ],
                    [
                      {
                        text: "LR.22 Stress Range: ",
                        bold: true,
                        margin: [0, 3, 0, 0],
                      },
                      {
                        text: element?.stressRange + " ksi",
                        margin: [0, 3, 0, 0],
                      },
                    ],
                    [
                      {
                        text: "B.EP.02 Legal Load Rating Factor: ",
                        bold: true,
                        margin: [0, 3, 0, 0],
                      },
                      {
                        text: element?.legalLoadRF ? Number.parseFloat(element?.legalLoadRF)?.toFixed(2) : "",
                        margin: [0, 3, 0, 0],
                      },
                    ],
                    [
                      {
                        text: "B.EP.03 Vehicle Posting Type: ",
                        bold: true,
                        margin: [0, 3, 0, 0],
                      },
                      {
                        text: configStore.getReferenceValue(REFERENCE_TABLE.VEHICLE_POSTING_TYPE, element?.vehPostType),
                        margin: [0, 3, 0, 0],
                      },
                    ],
                    [
                      {
                        text: "B.EP.04 Vehicle Posting Value: ",
                        bold: true,
                        margin: [0, 3, 0, 0],
                      },
                      {
                        text: element?.vehPostWeight + " Tons",
                        margin: [0, 3, 0, 0],
                      },
                    ],
                  ],
                },
                layout: "noBorders",
              },
            ],
          },
          {
            colSpan: 9,
            columns: [
              [
                {
                  text: "LR.23 Rating Dataset: ",
                  bold: true,
                  margin: [0, 3, 0, 0],
                },
                {
                  text: element?.supportDataset,
                },
                {
                  text: "LR.24 Notes: ",
                  bold: true,
                  margin: [0, 3, 0, 0],
                },
                {
                  text: element?.notes,
                },
              ],
            ],
          },
        ],
      },
    ],
  );
  return loadRatings;
}
export { setLoadRatingsDocDefinition, setLoadRatingsTunnelDocDefinition };
